import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button, Card, Checkbox, Flex } from 'antd';
import classnames from 'classnames';
import FadeIn from '../FadeIn';
import LoadingIndicator from '../LoadingIndicator';
import useApiClient from '../../hooks/api/useApiClient';
import useFeedbackLabel from '../../hooks/ui/useFeedbackLabel';
import useForm from '../../hooks/utility/useForm';
import { DeliveryMode } from '../../services/exports/Constants';
import { useGetServiceContractQuery, useSaveServiceContractMutation } from '../../store/api-slices';

export default function DeliveryStructureStep(props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Verification:DeliveryStructureStep' });

  const { buttonText, isSkippable, moveBackwards, moveForward, skip, skipping } = props;

  const { ServiceContractsManager } = useApiClient();
  // The contract is always created in an earlier onboarding step, so we only
  // obtain it here. `refetchOnMountOrArgChange` refetches it whenever this step
  // mounts so we always work with fresh data.
  const {
    data: contract,
    isLoading,
    isError,
  } = useGetServiceContractQuery({ apiManager: ServiceContractsManager }, { refetchOnMountOrArgChange: true });
  const [saveContract, { isLoading: saving }] = useSaveServiceContractMutation();

  const { setErrorFeedback, renderFeedbackLabel } = useFeedbackLabel({ t });

  const { data, update, setData } = useForm({
    delivery_modes: [DeliveryMode.Internal, DeliveryMode.External],
  });

  // Sync the form once the contract is (re)loaded.
  useEffect(() => {
    if (contract) {
      setData({
        delivery_modes: contract.delivery_modes ?? [],
      });
    }
  }, [contract]);

  useEffect(() => {
    if (isError) {
      toast.error(t('toasts.error'));
    }
  }, [isError]);

  const toggleMode = (mode) =>
    update(
      'delivery_modes',
      data.delivery_modes.includes(mode)
        ? data.delivery_modes.filter((item) => item !== mode)
        : [...data.delivery_modes, mode],
    );

  const save = async () => {
    const { error } = await saveContract({
      apiManager: ServiceContractsManager,
      data: { delivery_modes: data.delivery_modes },
    });

    if (error) {
      setErrorFeedback(error);

      return toast.error(t('toasts.error'));
    }

    return moveForward && moveForward();
  };

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <FadeIn>
      {renderFeedbackLabel}
      <div className="mb-lg">
        <p className="text-lg font-semibold">{t('title')}</p>
        <p className="text-gray-500 text-sm mt-xs">{t('subtitle')}</p>
      </div>
      <Flex vertical gap={12} className="mb-md">
        {Object.values(DeliveryMode).map((mode) => {
          const selected = data.delivery_modes.includes(mode);

          return (
            <Card
              key={`delivery-mode-${mode}`}
              hoverable
              onClick={() => toggleMode(mode)}
              className={classnames('cursor-pointer', { 'border-brand-primary': selected })}
              classNames={{ body: '!p-md' }}
            >
              <Flex gap={12} align="center">
                <Checkbox checked={selected} className="mr-none" />
                <p className="font-medium">{t(`form.delivery_mode.options.${mode}`)}</p>
              </Flex>
            </Card>
          );
        })}
      </Flex>
      <Flex justify="space-between">
        {isSkippable ? (
          <Button color="default" variant="solid" onClick={skip} loading={skipping}>
            {t('buttons.skip')}
          </Button>
        ) : (
          <span />
        )}
        <Flex justify="end">
          {moveBackwards && (
            <Button onClick={moveBackwards} className="mr-md">
              {t('buttons.back')}
            </Button>
          )}
          <Button type="primary" onClick={save} loading={saving} disabled={data.delivery_modes.length === 0}>
            {buttonText ?? t('buttons.next')}
          </Button>
        </Flex>
      </Flex>
    </FadeIn>
  );
}
